import { useState } from 'react';
import { ArrowRight, Check, X, Shield, Brain, Zap, Globe, Users, TrendingUp, Award, Lock, BarChart3 } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import MegaFooter from '@/components/MegaFooter';
import SEOHead from '@/components/SEOHead';
import { DemoModal } from '@/components/DemoModal';

var COMPARISON_CATEGORIES = [
  {
    category: "Platform Architecture",
    features: [
      { feature: "Multi-Tier Channel Hierarchy (5+ levels)", cl: true, capillary: false, zinrelo: false, almonds: true, xoxoday: false },
      { feature: "Role-Based Access (Manufacturer to Influencer)", cl: true, capillary: true, zinrelo: false, almonds: false, xoxoday: false },
      { feature: "Territory & Geography Mapping", cl: true, capillary: true, zinrelo: false, almonds: false, xoxoday: false },
      { feature: "No-Code Program Builder", cl: true, capillary: false, zinrelo: true, almonds: false, xoxoday: false },
      { feature: "Multi-Stakeholder Loyalty (Single Platform)", cl: true, capillary: false, zinrelo: false, almonds: false, xoxoday: false },
      { feature: "B2B-Native Architecture (Not B2C Adapted)", cl: true, capillary: false, zinrelo: false, almonds: true, xoxoday: false },
    ]
  },
  {
    category: "Data Capture & Verification",
    features: [
      { feature: "QR-Based Product Verification", cl: true, capillary: false, zinrelo: false, almonds: true, xoxoday: false },
      { feature: "Invoice Scan with AI-OCR", cl: true, capillary: true, zinrelo: false, almonds: false, xoxoday: false },
      { feature: "Secondary Sales (Sell-Out) Tracking", cl: true, capillary: false, zinrelo: false, almonds: true, xoxoday: false },
      { feature: "Influence-to-Sale Attribution", cl: true, capillary: false, zinrelo: false, almonds: false, xoxoday: false },
      { feature: "POS / ERP / DMS Integration", cl: true, capillary: true, zinrelo: false, almonds: true, xoxoday: false },
      { feature: "WhatsApp-Native Data Collection", cl: true, capillary: false, zinrelo: false, almonds: false, xoxoday: false },
    ]
  },
  {
    category: "AI & Intelligence",
    features: [
      { feature: "AI-Powered Churn Prediction", cl: true, capillary: true, zinrelo: false, almonds: false, xoxoday: false },
      { feature: "Scheme ROI Optimization Engine", cl: true, capillary: false, zinrelo: false, almonds: false, xoxoday: false },
      { feature: "Fraud & Anomaly Detection", cl: true, capillary: true, zinrelo: false, almonds: false, xoxoday: false },
      { feature: "Natural Language Analytics (Ask the Data)", cl: true, capillary: false, zinrelo: false, almonds: false, xoxoday: false },
      { feature: "Prescriptive Recommendations", cl: true, capillary: false, zinrelo: false, almonds: false, xoxoday: false },
      { feature: "Territory Performance Intelligence", cl: true, capillary: true, zinrelo: false, almonds: false, xoxoday: false },
    ]
  },
  {
    category: "Rewards & Payouts",
    features: [
      { feature: "Instant UPI Cash Payouts", cl: true, capillary: false, zinrelo: false, almonds: true, xoxoday: true },
      { feature: "500+ Brand Gift Vouchers", cl: true, capillary: true, zinrelo: true, almonds: true, xoxoday: true },
      { feature: "Physical Merchandise & Logistics", cl: true, capillary: true, zinrelo: false, almonds: false, xoxoday: true },
      { feature: "Travel & Experiential Rewards", cl: true, capillary: false, zinrelo: false, almonds: false, xoxoday: true },
      { feature: "KYC Verification for Payouts", cl: true, capillary: false, zinrelo: false, almonds: true, xoxoday: false },
      { feature: "Budget Governance & Approval Workflows", cl: true, capillary: true, zinrelo: false, almonds: false, xoxoday: false },
    ]
  },
  {
    category: "Engagement Channels",
    features: [
      { feature: "WhatsApp-First Engagement", cl: true, capillary: false, zinrelo: false, almonds: false, xoxoday: false },
      { feature: "Mobile App (Android + iOS)", cl: true, capillary: true, zinrelo: true, almonds: true, xoxoday: true },
      { feature: "Gamification & Leaderboards", cl: true, capillary: true, zinrelo: true, almonds: false, xoxoday: false },
      { feature: "Training & Certification Modules", cl: true, capillary: false, zinrelo: false, almonds: false, xoxoday: false },
      { feature: "Multi-Language Support (8+ Indian Languages)", cl: true, capillary: true, zinrelo: false, almonds: false, xoxoday: false },
      { feature: "SMS & Email Automation", cl: true, capillary: true, zinrelo: true, almonds: true, xoxoday: true },
    ]
  },
  {
    category: "Compliance & Governance",
    features: [
      { feature: "Automated TDS Calculation & Deduction", cl: true, capillary: false, zinrelo: false, almonds: true, xoxoday: false },
      { feature: "GST Compliance on Rewards", cl: true, capillary: false, zinrelo: false, almonds: true, xoxoday: false },
      { feature: "Full Audit Trail & Logging", cl: true, capillary: true, zinrelo: false, almonds: true, xoxoday: false },
      { feature: "ISO 27001 / SOC 2 Compliant", cl: true, capillary: true, zinrelo: true, almonds: false, xoxoday: true },
      { feature: "GDPR Ready", cl: true, capillary: true, zinrelo: true, almonds: false, xoxoday: true },
      { feature: "Role-Based Data Access Controls", cl: true, capillary: true, zinrelo: false, almonds: false, xoxoday: false },
    ]
  },
];

var TRUST_SIGNALS = [
  { icon: Shield, title: "ISO 27001 Compliant", desc: "Information security management certified" },
  { icon: Lock, title: "SOC 2 Type II", desc: "Enterprise-grade data security and privacy" },
  { icon: Globe, title: "GDPR Ready", desc: "Full data privacy compliance framework" },
  { icon: Award, title: "India's Fastest Growing", desc: "B2B loyalty platform by deployment velocity" },
];

var DIFFERENTIATORS = [
  {
    icon: Brain,
    title: "AI-Native, Not AI-Washed",
    desc: "Our AI engine runs on real transactional data — churn prediction, scheme optimization, fraud detection, and influence attribution. Not a chatbot slapped on a dashboard."
  },
  {
    icon: Zap,
    title: "Launch in Days, Not Months",
    desc: "No-code program builder lets you simulate, launch, modify, and rollback loyalty schemes instantly. Zero IT dependency for scheme changes."
  },
  {
    icon: Users,
    title: "Every Stakeholder, One Platform",
    desc: "Manufacturers, distributors, dealers, retailers, painters, electricians, mechanics — each gets role-specific loyalty logic on one unified platform."
  },
  {
    icon: TrendingUp,
    title: "Sell-Out Intelligence",
    desc: "QR-verified secondary sales data replaces unreliable billing claims. Track what actually sells to the end customer, not what sits in the warehouse."
  },
  {
    icon: BarChart3,
    title: "Measurable ROI from Day 1",
    desc: "Every rupee spent on loyalty is tracked to revenue outcome. See cost-per-acquisition, retention rates, and scheme ROI in real-time."
  },
  {
    icon: Globe,
    title: "Built for India, Scaled Globally",
    desc: "UPI payouts, WhatsApp integration, TDS automation, GST compliance, and multi-language support. Designed ground-up for Indian B2B complexity."
  },
];

function TickIcon() {
  return <Check size={16} className="text-[#7CC326]" />;
}

function CrossIcon() {
  return <X size={14} className="text-gray-300" />;
}

function ComparisonTable() {
  var rows = [];
  for (var c = 0; c < COMPARISON_CATEGORIES.length; c++) {
    var cat = COMPARISON_CATEGORIES[c];
    rows.push(
      <tr key={"cat-" + c}>
        <td colSpan={6} className="bg-gray-50 px-4 py-3 text-xs font-bold text-gray-900 uppercase tracking-wider border-b border-gray-200">
          {cat.category}
        </td>
      </tr>
    );
    for (var f = 0; f < cat.features.length; f++) {
      var feat = cat.features[f];
      rows.push(
        <tr key={"feat-" + c + "-" + f} className="border-b border-gray-100 hover:bg-gray-50/50 transition-colors">
          <td className="px-4 py-3 text-sm text-gray-700">{feat.feature}</td>
          <td className="px-2 py-3 text-center">{feat.cl ? <TickIcon /> : <CrossIcon />}</td>
          <td className="px-2 py-3 text-center">{feat.capillary ? <TickIcon /> : <CrossIcon />}</td>
          <td className="px-2 py-3 text-center">{feat.zinrelo ? <TickIcon /> : <CrossIcon />}</td>
          <td className="px-2 py-3 text-center">{feat.almonds ? <TickIcon /> : <CrossIcon />}</td>
          <td className="px-2 py-3 text-center">{feat.xoxoday ? <TickIcon /> : <CrossIcon />}</td>
        </tr>
      );
    }
  }
  return (
    <div className="overflow-x-auto" data-testid="comparison-table">
      <table className="w-full border-collapse min-w-[700px]">
        <thead>
          <tr className="bg-[#2C39B8] text-white">
            <th className="px-4 py-4 text-left text-sm font-semibold w-[40%]">Feature</th>
            <th className="px-2 py-4 text-center text-xs font-bold w-[12%]">Channel<br/>Loyalty</th>
            <th className="px-2 py-4 text-center text-xs font-medium w-[12%]">Capillary</th>
            <th className="px-2 py-4 text-center text-xs font-medium w-[12%]">Zinrelo</th>
            <th className="px-2 py-4 text-center text-xs font-medium w-[12%]">Almonds</th>
            <th className="px-2 py-4 text-center text-xs font-medium w-[12%]">Xoxoday</th>
          </tr>
        </thead>
        <tbody>{rows}</tbody>
        <tfoot>
          <tr className="bg-[#2C39B8]/5 border-t-2 border-[#2C39B8]">
            <td className="px-4 py-3 text-sm font-bold text-gray-900">Total Features</td>
            <td className="px-2 py-3 text-center text-sm font-bold text-[#2C39B8]">36/36</td>
            <td className="px-2 py-3 text-center text-sm font-semibold text-gray-600">18/36</td>
            <td className="px-2 py-3 text-center text-sm font-semibold text-gray-600">7/36</td>
            <td className="px-2 py-3 text-center text-sm font-semibold text-gray-600">13/36</td>
            <td className="px-2 py-3 text-center text-sm font-semibold text-gray-600">8/36</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

export default function WhyChannelLoyaltyPage() {
  var [demoOpen, setDemoOpen] = useState(false);

  return (
    <div data-testid="why-channelloyalty-page">
      <Navbar onDemoClick={function() { setDemoOpen(true); }} />
      <SEOHead
        title="Why ChannelLoyalty - Best B2B Channel Loyalty Platform India | ChannelLoyalty"
        description="Why India's top manufacturers choose ChannelLoyalty over traditional loyalty platforms. AI-native, instant UPI payouts, QR verification, WhatsApp engagement, and automated compliance."
        keywords="why ChannelLoyalty, best B2B loyalty platform India, ChannelLoyalty vs competitors, channel loyalty platform comparison, dealer loyalty software"
        canonical="https://channelloyalty.ai/why-channelloyalty"
      />

      {/* Banner Hero */}
      <section className="hero-banner-full">
        <div className="hero-banner-carousel">
          <div className="hero-banner-track">
            <div className="hero-banner-slide active">
              <img src="/images/banner_why_cl.jpg" alt="Why ChannelLoyalty - Built for How India Actually Works" />
            </div>
          </div>
        </div>
      </section>

      {/* Trust Signals */}
      <section className="py-12 bg-white border-b border-gray-100">
        <div className="container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {TRUST_SIGNALS.map(function(ts, idx) {
              var Icon = ts.icon;
              return (
                <div key={idx} className="text-center" data-testid={"trust-signal-" + idx}>
                  <Icon size={28} className="text-[#2C39B8] mx-auto mb-2" />
                  <h4 className="text-sm font-bold text-gray-900 mb-1">{ts.title}</h4>
                  <p className="text-xs text-gray-500">{ts.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Differentiators */}
      <section className="py-16 bg-white">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Our Edge</span>
            <h2>6 Reasons Enterprises Choose ChannelLoyalty</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {DIFFERENTIATORS.map(function(d, idx) {
              var Icon = d.icon;
              return (
                <div key={idx} className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow" data-testid={"differentiator-" + idx}>
                  <div className="w-10 h-10 rounded-lg bg-[#2C39B8]/8 flex items-center justify-center mb-4">
                    <Icon size={20} className="text-[#2C39B8]" />
                  </div>
                  <h4 className="text-base font-bold text-gray-900 mb-2">{d.title}</h4>
                  <p className="text-sm text-gray-600 leading-relaxed">{d.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Exhaustive Comparison Table */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Feature Comparison</span>
            <h2>ChannelLoyalty vs. Leading Platforms</h2>
            <p className="text-gray-500 text-sm mt-2">Comprehensive feature-by-feature comparison across 36 capabilities</p>
          </div>
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <ComparisonTable />
          </div>
          <p className="text-center text-xs text-gray-400 mt-4">Based on publicly available product documentation as of 2026. Contact us for detailed competitive analysis.</p>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="container relative z-10 text-center">
          <h2>See the Difference Yourself</h2>
          <p>Book a personalized demo and discover why enterprises choose ChannelLoyalty.</p>
          <div className="cta-buttons" style={{ justifyContent: 'center' }}>
            <Link to="/contact" className="btn btn-primary">Schedule Demo <ArrowRight size={18} /></Link>
          </div>
        </div>
      </section>

      <MegaFooter />
      <DemoModal isOpen={demoOpen} onClose={function() { setDemoOpen(false); }} />
    </div>
  );
}
